import { api } from './api';

interface RefreshResult {
    success: boolean;
    token?: string;
    user?: any;
    error?: string;
}

class TokenRefreshService {
    private isRefreshing = false;
    private refreshPromise: Promise<RefreshResult> | null = null;

    /**
     * Attempt to refresh the JWT token for email/password authenticated users
     */
    async refreshToken(): Promise<RefreshResult> {
        // Prevent multiple simultaneous refresh attempts
        if (this.isRefreshing && this.refreshPromise) {
            console.log(
                '[TokenRefresh] Already refreshing, waiting for result'
            );
            return this.refreshPromise;
        }

        this.isRefreshing = true;
        this.refreshPromise = this.performRefresh();

        try {
            const result = await this.refreshPromise;
            return result;
        } finally {
            this.isRefreshing = false;
            this.refreshPromise = null;
        }
    }

    private async performRefresh(): Promise<RefreshResult> {
        const authMethod = localStorage.getItem('authMethod');
        const storedUser = localStorage.getItem('pairUser');

        console.log(
            '[TokenRefresh] Attempting token refresh for auth method:',
            authMethod
        );

        if (!authMethod || !storedUser) {
            console.log('[TokenRefresh] No auth method or user data found');
            return { success: false, error: 'No authentication data found' };
        }

        // For email/password authentication, we need to re-authenticate
        if (authMethod === 'email') {
            // Parse stored user data
            let userData;
            try {
                userData = JSON.parse(storedUser);
            } catch (e) {
                console.error(
                    '[TokenRefresh] Failed to parse stored user data'
                );
                return { success: false, error: 'Invalid stored user data' };
            }

            // Check if we have stored credentials (not recommended for production)
            // In a production environment, you should use refresh tokens instead
            const storedEmail = userData.email;
            const storedPassword = sessionStorage.getItem('tempAuthPass'); // Temporary storage

            if (!storedEmail) {
                console.log('[TokenRefresh] No email found for refresh');
                return { success: false, error: 'No email found' };
            }

            // If we don't have the password, we can't refresh for email auth
            // This is a limitation of not having proper refresh tokens
            if (!storedPassword) {
                console.log(
                    '[TokenRefresh] Cannot refresh email auth without password'
                );
                return {
                    success: false,
                    error: 'Cannot refresh without credentials',
                };
            }

            try {
                // Re-authenticate to get a new JWT token
                console.log(
                    '[TokenRefresh] Re-authenticating with email/password'
                );
                const result = await api.signInUser({
                    email: storedEmail,
                    password: storedPassword,
                });

                if (result.success && result.data) {
                    console.log('[TokenRefresh] Token refresh successful');

                    // Update stored user data with new token
                    const updatedUser = {
                        ...userData,
                        token: result.data.token,
                    };
                    localStorage.setItem(
                        'pairUser',
                        JSON.stringify(updatedUser)
                    );

                    // Clear the temporary password after successful refresh
                    sessionStorage.removeItem('tempAuthPass');

                    return {
                        success: true,
                        token: result.data.token,
                        user: updatedUser,
                    };
                }
            } catch (error) {
                console.error('[TokenRefresh] Failed to refresh token:', error);
                return {
                    success: false,
                    error:
                        error instanceof Error
                            ? error.message
                            : 'Failed to refresh token',
                };
            }
        }

        // For social auth, delegate to silent auth service
        if (authMethod === 'google' || authMethod === 'apple') {
            console.log(
                '[TokenRefresh] Social auth detected, using silent auth service'
            );
            // This would use the existing silentAuth service
            const { silentAuthService } = await import('./silentAuth');
            const success = await silentAuthService.attemptSilentReauth();
            return {
                success,
                error: success ? undefined : 'Silent re-authentication failed',
            };
        }

        return { success: false, error: 'Unknown authentication method' };
    }

    /**
     * Store temporary authentication data for refresh purposes
     * WARNING: This is not secure and should only be used for development
     * In production, implement proper refresh tokens on the backend
     */
    storeTemporaryAuthData(email: string, password: string) {
        // Store in sessionStorage so it's cleared when the browser is closed
        sessionStorage.setItem('tempAuthPass', password);
        console.log('[TokenRefresh] Temporary auth data stored for refresh');
    }

    /**
     * Clear temporary authentication data
     */
    clearTemporaryAuthData() {
        sessionStorage.removeItem('tempAuthPass');
        console.log('[TokenRefresh] Temporary auth data cleared');
    }
}

export const tokenRefreshService = new TokenRefreshService();
